import { useState, type ReactNode } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";

import { cn } from "@/lib/utils";

const MAX_DISPLAY_COUNT = 9;

export const RecordsSection = ({
  children,
  collapsible = false,
  count,
  countControl,
  isFirst = false,
  title,
  titleActions,
}: {
  children: ReactNode;
  collapsible?: boolean;
  count?: number;
  countControl?: ReactNode;
  isFirst?: boolean;
  title: string;
  titleActions?: ReactNode;
}) => {
  /* React Hooks */
  const [isOpen, setIsOpen] = useState(count !== 0);

  /* Helpers */
  const displayCount =
    count === undefined
      ? null
      : count > MAX_DISPLAY_COUNT
        ? `${MAX_DISPLAY_COUNT.toString()}+`
        : String(count);

  /* Render */
  const render = () => {
    return (
      <Column className={cn("gap-6", isFirst ? "pt-0" : "pt-8")}>
        <Row className="items-center justify-between gap-2">
          <Row className="items-center gap-2">
            <Text className="text-base font-medium text-slate-900">
              {title}
            </Text>
            {countControl ?? (displayCount !== null ? (
              <Text className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {displayCount}
              </Text>
            ) : null)}
            {collapsible ? (
              <Button
                className="h-6 w-6 shrink-0 text-slate-400 hover:text-slate-600"
                onClick={() => {
                  setIsOpen((current) => !current);
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                {isOpen ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </Button>
            ) : null}
          </Row>
          {collapsible && !isOpen ? null : titleActions}
        </Row>
        {collapsible && !isOpen ? null : children}
      </Column>
    );
  };

  return render();
};
